import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, TrendingUp, ArrowUpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const UsageLimitWarning = ({ tenant, usage, planConfig }) => {
  const [overLimit, setOverLimit] = useState([]);

  useEffect(() => {
    if (!usage || !planConfig) return;

    const limits = planConfig.limits || {};
    const warnings = [];

    // Check drivers
    if (limits.max_drivers > 0 && usage.drivers >= limits.max_drivers) {
      warnings.push({
        resource: 'Drivers',
        current: usage.drivers,
        max: limits.max_drivers,
        percent: Math.round((usage.drivers / limits.max_drivers) * 100)
      });
    }

    // Check locations
    if (limits.max_locations > 0 && usage.locations >= limits.max_locations) {
      warnings.push({
        resource: 'Locations',
        current: usage.locations,
        max: limits.max_locations,
        percent: Math.round((usage.locations / limits.max_locations) * 100)
      });
    }

    // Check deliveries (monthly)
    const deliveriesCount = tenant?.current_usage?.deliveries_count || 0;
    if (limits.max_deliveries_per_month > 0 && deliveriesCount >= limits.max_deliveries_per_month) {
      warnings.push({
        resource: 'Deliveries (Monthly)',
        current: deliveriesCount,
        max: limits.max_deliveries_per_month,
        percent: Math.round((deliveriesCount / limits.max_deliveries_per_month) * 100)
      });
    }

    setOverLimit(warnings);
  }, [usage, planConfig, tenant]);

  if (overLimit.length === 0) return null;

  return (
    <Card className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
            <AlertCircle className="h-5 w-5" />
            Limit Reached - Upgrade Required
          </CardTitle>
          <Badge variant="destructive">Action Required</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {overLimit.map((warning) => (
          <div key={warning.resource} className="flex items-center justify-between p-3 bg-white dark:bg-zinc-900 rounded-lg border border-orange-200 dark:border-orange-800">
            <div>
              <div className="font-semibold text-orange-700 dark:text-orange-400">
                {warning.resource}: {warning.current} / {warning.max}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {warning.percent}% of limit used
              </div>
            </div>
            <Badge variant="destructive">{warning.percent}%</Badge>
          </div>
        ))}

        <div className="flex gap-2 pt-2">
          <Button asChild variant="default" className="flex-1">
            <Link to={`/super-admin/tenants/${tenant.tenant_id}`}>
              <ArrowUpCircle className="h-4 w-4 mr-2" />
              Upgrade Plan
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/super-admin/tenants/${tenant.tenant_id}/usage-history`}>
              <TrendingUp className="h-4 w-4 mr-2" />
              View History
            </Link>
          </Button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          This tenant has reached their plan limits. Upgrade to Pro or Enterprise for higher limits.
        </p>
      </CardContent>
    </Card>
  );
};

export default UsageLimitWarning;
